import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { useResourcesContext } from '../resource/ResourceContext';
import { useDashboardContext } from '../dashboard/DashboardProvider';
import IconButton from '@mui/material/IconButton';
import Profile from './Profile';
import { useTranslation } from 'react-i18next';
import * as authService from '../auth/authService';
import * as userService from '../user/userService';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { styled } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import { Tooltip } from '@mui/material';
import * as dashboardService from '../dashboard/dashboardService';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import './AppMenu.css';
import LeftMenu from './LeftMenu';
import * as userSavedColumnOrderService from '../usersavedcolumnorder/userSavedColumnOrderService';
import FavoriteDashboardSearch from '../components/FavoriteDashboardSearch';
import { getAdminShellMenuItems } from '../adminshell/adminShellMenu';
import { isAdminShell } from '../adminshell/adminShellConfig';
import AiAssistant from '../aiassistant/AiAssistantIframe';
import SmartDataAnalyst from '../smartdataanalyst/SmartDataAnalyst';

export default function AppMenu(props) {
  const { children } = props;
  const { t } = useTranslation();
  const { componentTitle, selectedMenuItem, loggedInUserInfo, collapse, setCollapse, isWebViews, isMobileView, hideLeftMenu } = useResourcesContext();
  const leftMenuItems = [];
  const leftMenuItemDashboard = [];
  const [sortedLeftMenuItems, setSortedLeftMenuItems] = useState([]);
  const [isSorted, setIsSorted] = useState(false);

  const isLogin = loggedInUserInfo?.id !== null;
  const isAdminShellMode = isAdminShell(selectedMenuItem);
  const hasCustomDashboardAccessRight = authService.hasAccessRight(loggedInUserInfo, authService.accessRights.CustomDashboards);
  const hasCustomDashboardViewRight = hasCustomDashboardAccessRight && authService.hasModuleViewRight(loggedInUserInfo, authService.modules.CustomDashboards);

  const hasNotificationViewRight = authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Notifications);
  const hasMessageViewRight = authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Message);
  const [favoriteDashboards, setFavoriteDashboards] = useState([]);
  const { webDashboardId, setWebDashboardId } = useDashboardContext();
  const isHoveringRef = useRef(false);
  const [isIframeVisible, setIsIframeVisible] = useState(false);
  const [checkNumberNotification, setCheckNumberNotification] = useState(0);
  const [checkNumberMessage, setCheckNumberMessage] = useState(0);
  const [iframeSrc, setIframeSrc] = useState(`${process.env.REACT_APP_FIELDPIE_OLD_URL}/notificationv2/`);
  const [iframeType, setIframeType] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    const fetchFavoriteDashboards = async () => {
      if (loggedInUserInfo == null || loggedInUserInfo.id == null) {
        return;
      }

      try {
        const dashboards = await dashboardService.dashboardMenuItems();
        setFavoriteDashboards(dashboards);
      } catch (error) {
        console.error(error);
      }
    };

    if (hasCustomDashboardViewRight != null && hasCustomDashboardViewRight) {
      fetchFavoriteDashboards();
    }
  }, [loggedInUserInfo]);

  useEffect(() => {
    async function fetchCustomDashboardId() {
      try {
        const id = await dashboardService.webDashboardId();
        setWebDashboardId(id);
      } catch (error) {
        console.error(error);
      }
    }

    if (hasCustomDashboardViewRight != null && hasCustomDashboardViewRight) {
      fetchCustomDashboardId();
    }
  }, [loggedInUserInfo, setWebDashboardId]);

  const handleMouseEnter = () => {
    isHoveringRef.current = true;
  };

  const handleMouseLeave = () => {
    isHoveringRef.current = false;
  };

  leftMenuItemDashboard.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Dashboard),
    selected: selectedMenuItem === 'dashboard',
    label: t('dashboard_menu'),
    path: webDashboardId > 0 ? `/dashboard/${webDashboardId}` : '/dashboard/',
    image: 'dashboard-stacked-icon.png',
    bgColor: '#1d252c',
    fieldPie2: webDashboardId > 0,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave
  });

  leftMenuItems.push({
    visible: hasCustomDashboardViewRight,
    selected: selectedMenuItem === 'customdashboards',
    label: t('custom_dashboards'),
    path: '/dashboards',
    key: 'customDashboardsMenuItem',
    image: 'small-report-icon.png',
    bgColor: '#009587',
    menuItemKey: 'customdashboards',
    fieldPie2: true
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Reschedule),
    selected: selectedMenuItem === 'reschedule',
    label: t('reschedule'),
    path: '/reschedule/',
    key: 'rescheduleMenuItem',
    image: 'kpi-stacked-icon.png',
    bgColor: '#9e9e9eb2'
  });

  const hasWorkOrders = authService.hasAccessRight(loggedInUserInfo, authService.accessRights.WorkOrder);

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Plans),
    selected: selectedMenuItem === 'schedules',
    label: hasWorkOrders ? t('scheduling') : t('agenda'),
    path: hasWorkOrders ? '/schedules/' : '/plans/',
    key: 'plansMenuItem',
    image: 'agenda-stacked-icon.png',
    bgColor: '#ba68c8'
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Tasks),
    selected: selectedMenuItem === 'tasks',
    label: t('tasks'),
    path: '/tasks/',
    key: 'tasksMenuItem',
    image: 'completed-tasks.png',
    bgColor: '#607d8b'
  });

  const hasVisits = authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Visits);
  const hasJobs = authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Jobs);

  leftMenuItems.push({
    visible: hasJobs || hasVisits,
    selected: selectedMenuItem === 'jobs' || selectedMenuItem === 'visitsMap',
    label: hasJobs || authService.isService(loggedInUserInfo) ? t('jobs') : t('visits'),
    path: '/jobs/',
    key: 'visitsMenuItem',
    image: 'visits-stacked-icon.png',
    bgColor: '#009587'
  });

  const hasTargetAccess = authService.hasAccessRight(loggedInUserInfo, authService.accessRights.Targets);

  leftMenuItems.push({
    visible: hasTargetAccess && authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Targets),
    selected: selectedMenuItem === 'targets',
    label: t('targets'),
    path: '/targets/',
    menuItemKey: 'targets',
    image: 'agenda-stacked-icon.png',
    key: 'tatgetMenuItem',
    bgColor: '#009587',
    fieldPie2: true
  });

  const hasSchedulingWizardAccess = authService.hasAccessRight(loggedInUserInfo, authService.accessRights.SchedulingWizard);

  const hasHelpCenterAccess = authService.hasAccessRight(loggedInUserInfo, authService.accessRights.HelpCenter);
  const hasHelpCenterView = authService.hasModuleViewRight(loggedInUserInfo, authService.modules.HelpCenter);
  const hasSmartDataAnalystAccess = authService.hasAccessRight(loggedInUserInfo, authService.accessRights.SmartDataAnalyst);
  const hasSmartDataAnalystView = authService.hasModuleViewRight(loggedInUserInfo, authService.modules.SmartDataAnalyst);

  leftMenuItems.push({
    visible: hasSchedulingWizardAccess && authService.hasModuleViewRight(loggedInUserInfo, authService.modules.SchedulingWizard),
    selected: selectedMenuItem === 'schedulingwizard',
    label: t('scheduling_wizard'),
    path: '/schedulingwizard/',
    menuItemKey: 'schedulingwizard',
    image: 'agenda-stacked-icon.png',
    key: 'schedulingWizardMenuItem',
    bgColor: '#5c6bc0',
    fieldPie2: true
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Activities),
    selected: selectedMenuItem === 'activities',
    label: t('activities'),
    path: '/activities/',
    key: 'activitiesMenuItem',
    image: 'custom-activities-stacked-icon.png',
    bgColor: '#a52a2a'
  });

  const hasCustomActivityAccess = authService.hasAccessRight(loggedInUserInfo, authService.accessRights.CustomActivity);

  leftMenuItems.push({
    visible: hasCustomActivityAccess && authService.hasModuleViewRight(loggedInUserInfo, authService.modules.CustomForms),
    selected: selectedMenuItem === 'form',
    label: t('forms'),
    path: '/form/',
    image: 'custom-form-activities-icon.png',
    key: 'customFormsMenuItem',
    bgColor: '#4fc3f7',
    menuItemKey: 'form',
    fieldPie2: true
  });

  const hasDistributorNetworkViewRight = authService.hasModuleViewRight(loggedInUserInfo, authService.modules.DistributorNetwork);

  leftMenuItems.push({
    visible: hasDistributorNetworkViewRight,
    selected: selectedMenuItem === 'distributornetwork',
    label: t('distributor_network'),
    path: '/distributornetworks',
    key: 'distributorNetworkMenuItem',
    image: 'corporate-fare-icon.png',
    bgColor: '#a52a2a',
    menuItemKey: 'distributornetwork',
    fieldPie2: true
  });

  const hasWarehouseViewRight = authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Warehouses);

  leftMenuItems.push({
    visible: hasWarehouseViewRight,
    selected: selectedMenuItem === 'warehouse',
    label: t('warehouses'),
    path: '/warehouses',
    key: 'wareHousesMenuItem',
    image: 'store-icon.png',
    bgColor: '#ff9800',
    menuItemKey: 'warehouse',
    fieldPie2: true
  });

  const hasStockViewRight = authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Stocks);

  leftMenuItems.push({
    visible: hasWarehouseViewRight && hasStockViewRight,
    selected: selectedMenuItem === 'warehousetransfer',
    label: t('warehouse_transfers'),
    path: '/warehousetransfers',
    key: 'warehouseTransfersMenuItem',
    image: 'local-shipping-icon.png',
    menuItemKey: 'warehousetransfer',
    bgColor: '#4fc3f7',
    fieldPie2: true
  });

  leftMenuItems.push({
    visible: hasStockViewRight,
    selected: selectedMenuItem === 'stockmovement',
    label: t('stock_movements'),
    path: '/stockmovements',
    key: 'stockMovementsMenuItem',
    image: 'swap-vert-icon.png',
    bgColor: '#009587',
    menuItemKey: 'stockmovement',
    fieldPie2: true
  });

  const hasQueueViewRight = authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Queue);

  leftMenuItems.push({
    visible: hasQueueViewRight,
    selected: selectedMenuItem === 'bulkoperations',
    label: t('bulk_operations'),
    path: '/bulkoperations',
    key: 'queueMenuItem',
    image: 'downloading-icon.png',
    bgColor: '#4fc3f7',
    menuItemKey: 'bulkoperations',
    fieldPie2: true
  });

  const hasInvoices = authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Invoices);

  leftMenuItems.push({
    visible: hasInvoices,
    selected: selectedMenuItem === 'invoices',
    label: t('invoices'),
    path: '/invoices/',
    key: 'invoicesMenuItem',
    image: 'invoice.png',
    bgColor: '#079607'
  });

  const hasQuoteAccess = authService.hasAccessRight(loggedInUserInfo, authService.accessRights.PurchaseOrder);
  const quoteMenuItemlabel = loggedInUserInfo?.usePurchaseOrder
    ? t('purchase_order_menu')
    : authService.hasAccessRight(loggedInUserInfo, authService.accessRights.Bip)
      ? t('quotes_and_orders')
      : t('quotes');

  leftMenuItems.push({
    visible: hasQuoteAccess && hasInvoices,
    selected: selectedMenuItem === 'quotes',
    label: quoteMenuItemlabel,
    path: '/quotes/',
    key: 'purchaseOrderMenuItem',
    image: 'custom-activities-stacked-icon.png',
    bgColor: '#079607'
  });

  const hasClients = authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Clients);

  leftMenuItems.push({
    visible: hasClients,
    selected: selectedMenuItem === 'clients' || selectedMenuItem === 'clientsMap',
    label: loggedInUserInfo?.clientNamePlural,
    path: '/clients/',
    key: 'clientsMenuItem',
    image: 'clients-stacked-icon.png',
    bgColor: '#ff9800'
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Users),
    selected: selectedMenuItem === 'users',
    label: loggedInUserInfo?.planogramUser ? t('user_managment') : t('users'),
    path: '/user/',
    key: 'usersMenuItem',
    image: 'field-force-stacked-icon.png',
    bgColor: '#03a9f4'
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Tagging),
    selected: selectedMenuItem === 'tagging',
    label: t('tagging'),
    path: '/tagging/',
    key: 'taggingMenuItem',
    image: 'custom-form-activities-icon.png',
    bgColor: '#03a9f4',
    menuItemKey: 'tagging',
    fieldPie2: true
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.ModelTraining),
    selected: selectedMenuItem === 'training',
    label: t('model_training'),
    path: '/training/',
    key: 'modelTrainingMenuItem',
    image: 'custom-form-activities-icon.png',
    bgColor: '#03a9f4',
    menuItemKey: 'training',
    fieldPie2: true
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.PredictionAndAnalysis),
    selected: selectedMenuItem === 'prediction',
    label: t('prediction'),
    path: '/prediction/',
    key: 'predictionMenuItem',
    image: 'custom-form-activities-icon.png',
    bgColor: '#03a9f4',
    menuItemKey: 'prediction',
    fieldPie2: true
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Planogram),
    selected: selectedMenuItem === 'planogram',
    label: t('planogram'),
    path: '/planogram/',
    key: 'planogramMenuItem',
    image: 'custom-form-activities-icon.png',
    bgColor: '#03a9f4',
    menuItemKey: 'planogram',
    fieldPie2: true
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.MLProduct),
    selected: selectedMenuItem === 'mlproducts',
    label: t('mlproducts'),
    path: '/mlproducts/',
    key: 'mlProductMenuItem',
    image: 'products-stacked-icon.png',
    bgColor: '#03a9f4',
    menuItemKey: 'mlproducts',
    fieldPie2: true
  });

  const hasProducts =
    authService.hasAccessRight(loggedInUserInfo, authService.accessRights.Product) &&
    authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Products);

  leftMenuItems.push({
    visible: hasProducts,
    selected: selectedMenuItem === 'products',
    label: t('products'),
    path: '/products/',
    key: 'productsMenuItem',
    image: 'products-stacked-icon.png',
    bgColor: '#ff5722'
  });

  const hasProductsV2 =
    authService.hasAccessRight(loggedInUserInfo, authService.accessRights.ProductV2) &&
    authService.hasModuleViewRight(loggedInUserInfo, authService.modules.ProductsV2);

  leftMenuItems.push({
    visible: hasProductsV2,
    selected: selectedMenuItem === 'productsV2',
    label: t('products'),
    path: '/productsv2/',
    key: 'productsV2MenuItem',
    image: 'products-stacked-icon.png',
    bgColor: '#ff5722',
    menuItemKey: 'productsV2',
    fieldPie2: true
  });

  leftMenuItems.push({
    visible: authService.hasModuleEditRight(loggedInUserInfo, authService.modules.PredictFields),
    selected: selectedMenuItem === 'predict',
    label: t('addAIPower'),
    path: '/customforms/?addAIPower=true',
    key: 'predictFieldsMenuItem',
    image: 'stars.png',
    bgColor: '#a52a2a'
  });

  const hasExternalSchedules = authService.hasModuleViewRight(loggedInUserInfo, authService.modules.ExternalSchedules);
  const is123BT = authService.is123BT(loggedInUserInfo);

  leftMenuItems.push({
    visible: hasExternalSchedules,
    selected: selectedMenuItem === 'clientjobrequests',
    label: is123BT ? t('externalSchedules') : t('clientJobRequests_parameter', { clientNameSingular: loggedInUserInfo?.clientNameSingular }),
    path: is123BT ? '/externalschedules/' : '/clientjobrequests/',
    key: is123BT ? 'one23ClientJobRequestsMenuItem' : 'clientJobRequestsMenuItem',
    image: 'visits-stacked-icon.png',
    bgColor: '#a52a2a'
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Documents),
    selected: selectedMenuItem === 'documents',
    label: t('documents'),
    path: '/documents/',
    key: 'documentsMenuItem',
    image: 'file-document.png',
    bgColor: '#b84343'
  });

  leftMenuItems.push({
    visible:
      authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Inventory) ||
      authService.hasModuleViewRight(loggedInUserInfo, authService.modules.InventoryAssingedItems),
    selected: selectedMenuItem === 'inventorylist',
    label: t('inventory_management'),
    path: '/inventorylist/',
    key: 'inventoryMenuItem',
    image: 'custom-activities-stacked-icon.png',
    bgColor: '#ff5722'
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.WarrantyPeriod),
    selected: selectedMenuItem === 'warrantyperiods',
    label: t('warranty_periods'),
    path: '/warrantyperiods/',
    key: 'warrantyPeriodeMenuItem',
    image: 'feedback.png',
    bgColor: '#BF4E1D',
    menuItemKey: 'warrantyperiods',
    fieldPie2: true
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.DocumentManagement),
    selected: selectedMenuItem === 'documentManagement',
    label: t('document_management'),
    path: '/bipdocumentlist/',
    key: 'documentManagementMenuItem',
    image: 'file-document.png',
    bgColor: '#b84343'
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.PopupManagement),
    selected: selectedMenuItem === 'popup',
    label: t('popup_management'),
    path: '/popuplist/',
    key: 'popupMenuItem',
    image: 'popup-icon.png',
    bgColor: '#ff5722'
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Campaigns),
    selected: selectedMenuItem === 'campaign',
    label: t('campaigns'),
    path: '/campaigns/',
    key: 'campaignMenuItem',
    image: 'campaign.png',
    bgColor: '#4fc3f7',
    menuItemKey: 'campaign',
    fieldPie2: true
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.BipPromotion),
    selected: selectedMenuItem === 'bippromotion',
    label: t('bip_promotions'),
    path: '/bippromotions/',
    key: 'bipPromotionMenuItem',
    image: 'bip-price-icon.png',
    bgColor: '#ed1c24',
    menuItemKey: 'bippromotion',
    fieldPie2: true
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.BipStatistics),
    selected: selectedMenuItem === 'bipstatistics',
    label: t('bip_statistics'),
    path: '/bipstatistics/',
    key: 'bipStatisticsMenuItem',
    image: 'custom-activities-stacked-icon.png',
    bgColor: '#03a9f4',
    fieldPie2: true,
    menuItemKey: 'bip_statistics'
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.BipLiveStream),
    selected: selectedMenuItem === 'biplivestream',
    label: t('bip_live_stream'),
    path: '/biplivestreams/',
    key: 'bipLiveStreamMenuItem',
    image: 'live-stream-icon.png',
    bgColor: '#A52A2A'
  });

  leftMenuItems.push({
    visible: authService.hasModuleViewRight(loggedInUserInfo, authService.modules.InternalMessage),
    selected: selectedMenuItem === 'internalmessages',
    label: t('internal_messages'),
    path: '/internalmessages/',
    key: 'internalMessagesMenuItem',
    image: 'message-icon.png',
    bgColor: '#03a9f4',
    fieldPie2: true,
    menuItemKey: 'internalmessages'
  });

  const hasReportsViewRight = authService.hasModuleViewRight(loggedInUserInfo, authService.modules.Reports);
  leftMenuItems.push({
    visible: hasReportsViewRight,
    selected: selectedMenuItem === 'reports',
    label: t('reports'),
    path: '/reports',
    key: 'reportsMenuItem',
    image: 'reports.png',
    bgColor: '#009587',
    menuItemKey: 'reports',
    fieldPie2: true
  });

  const drawerWidth = 240;
  const openedMixin = (theme) => ({
    width: drawerWidth,
    border: 'none',
    transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }),
    overflowX: 'hidden'
  });

  const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    border: 'none',
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: { width: `calc(${theme.spacing(8)} + 1px)` }
  });

  const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme)
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme)
    })
  }));

  const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar
  }));

  const toggleMenu = async () => {
    const newMenuState = !collapse;
    setCollapse(newMenuState);

    try {
      await userService.updateMenuClosed({ menuClosed: newMenuState });
      localStorage.setItem('menuClosed', newMenuState);
    } catch (e) {
      console.error(e);
    }
  };

  const toggleSidebar = () => {
    if (!collapse) setCollapse(true);
    const toogle = document.querySelector('.toogle');
    toogle.classList.add('hidden');
    const sidebar = document.querySelector('.sidebar');
    const children = document.querySelector('.children');
    const navbarHeader = document.querySelector('.navbarHeader');
    const navbarBrand4 = document.querySelector('.navbar-brand4');
    const navbarBrand5 = document.querySelector('.navbar-brand5');
    if (sidebar.classList.contains('open')) {
      navbarHeader.classList.add('hidden');
      // Close sidebar
      children.classList.remove('open');
      sidebar.classList.remove('open');
      navbarBrand4.classList.add('hidden');
      navbarBrand4.classList.remove('open');
      navbarBrand5.classList.remove('hidden');
      navbarBrand5.classList.add('open');
    } else {
      children.classList.add('open');
      navbarHeader.classList.add('hidden');
      // Open sidebar
      sidebar.classList.add('open');
      navbarBrand4.classList.add('open');
      navbarBrand4.classList.remove('hidden');
      navbarBrand5.classList.remove('open');
      navbarBrand5.classList.add('hidden');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (iframeRef.current && !iframeRef.current.contains(event.target)) {
        setIsIframeVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== process.env.REACT_APP_FIELDPIE_OLD_URL) {
        return;
      }

      if (event.data.type === 'notificationHTML') {
        const numberValue = parseInt(event.data.content);
        setCheckNumberNotification(numberValue);

        const container = document.getElementById('notification-container');
        if (container) {
          container.innerHTML = numberValue;
        }
      } else if (event.data.type === 'messageHTML') {
        const numberValue = parseInt(event.data.content);
        setCheckNumberMessage(numberValue);

        const container = document.getElementById('message-container');
        if (container) {
          container.innerHTML = numberValue;
        }
      }
    };

    window.addEventListener('message', handleMessage, false);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    function redirectNotification(event) {
      if (event.data && event.data.url) {
        let path = event.data.url;

        if (path.includes('form')) {
          window.location.href = path;
        } else {
          path = path.replace('/fieldpie', '');
          const baseUrl = process.env.REACT_APP_FIELDPIE_OLD_URL;
          const redirectUrl = `${baseUrl}${path}`;
          window.location.href = redirectUrl;
        }
      }
    }

    window.addEventListener('message', redirectNotification);
    return () => window.removeEventListener('message', redirectNotification);
  }, []);

  const toggleIframe = (type) => {
    if (isIframeVisible && iframeType === type) {
      setIsIframeVisible(false);
    } else {
      setIsIframeVisible(false);
      setIframeSrc('');
      setTimeout(() => {
        setIframeSrc(`${process.env.REACT_APP_FIELDPIE_OLD_URL}/notificationv2/?type=${type}`);
        setIframeType(type);
        setIsIframeVisible(true);
      }, 100);
    }
  };

  const fetchAndOrderLeftMenu = async () => {
    if (!isLogin || isSorted || isAdminShellMode) {
      return;
    }

    const orderedKeysString = await userSavedColumnOrderService.getLeftMenuOrder();

    if (orderedKeysString !== undefined) {
      const orderedKeys = orderedKeysString.data;

      leftMenuItems.sort((a, b) => {
        const aIndex = orderedKeys.indexOf(a.key);
        const bIndex = orderedKeys.indexOf(b.key);

        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }

        if (aIndex !== -1) {
          return -1;
        }
        if (bIndex !== -1) {
          return 1;
        }

        return 0;
      });

      setSortedLeftMenuItems(leftMenuItems);
      setIsSorted(loggedInUserInfo !== null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (loggedInUserInfo == null || loggedInUserInfo.id == null) {
        return;
      }

      await fetchAndOrderLeftMenu();
    };

    fetchData();
  }, [loggedInUserInfo]);

  const FavoriteDashboardButton = React.memo(({ onClick }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleFavoriteDashboardsClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    return (
      <>
        <IconButton id="favDash" className="favdashboard-search-icon" onClick={handleFavoriteDashboardsClick}>
          <ChevronRightIcon style={{ height: '28px', width: '28px', transform: 'rotate(90deg)' }} />
        </IconButton>
        <FavoriteDashboardSearch dashboards={favoriteDashboards} anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
      </>
    );
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <CssBaseline />
      {isLogin && !isWebViews && hasSmartDataAnalystAccess && hasSmartDataAnalystView && (
        <SmartDataAnalyst rightOffset={hasHelpCenterAccess && hasHelpCenterView ? 92 : 24} />
      )}
      {isLogin && !isWebViews && hasHelpCenterAccess && hasHelpCenterView && <AiAssistant />}
      <Box
        sx={{
          display: isWebViews ? 'none' : 'flex',
          backgroundColor: 'primary.main',
          minHeight: '64px',
          height: '64px',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {!hideLeftMenu && (
          <div onClick={toggleSidebar} style={{ position: 'fixed', left: 25 }}>
            <a className="navbar-brand4" href="#">
              <img alt="up arrow" src="../../img/up-arrow.png" />
            </a>
            <a className="navbar-brand5" href="#">
              <img alt="down arrow" src="../../img/down-arrow-grey.png" />
            </a>
          </div>
        )}
        <Box sx={{ mr: hideLeftMenu ? 2 : collapse ? 15 : 28 }}></Box>

        <Box sx={{ color: '#ffffffd6', fontSize: 22, ml: -5, fontWeight: 500 }}>{componentTitle}</Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', mr: '30px' }}>
          <Profile />
          {hasMessageViewRight && (
            <div className="notification-wrapper">
              {checkNumberMessage > 0 && (
                <span id="message-container" className="notification-badge">
                  {checkNumberMessage}
                </span>
              )}
              <ChatOutlinedIcon onClick={() => toggleIframe('message')} sx={{ cursor: 'pointer', color: 'white', fontSize: '24px', mr: 1 }} />
            </div>
          )}
          {hasNotificationViewRight && (
            <div className="notification-wrapper">
              {checkNumberNotification > 0 && (
                <span id="notification-container" className="notification-badge">
                  {checkNumberNotification}
                </span>
              )}
              <NotificationsNoneIcon onClick={() => toggleIframe('notification')} sx={{ cursor: 'pointer', color: 'white', fontSize: '24px', ml: 2 }} />
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            scrolling="no"
            style={{
              width: '100%',
              height: '100%',
              position: 'fixed',
              top: '0',
              right: '0',
              clipPath: 'inset(0 0 0 calc(100% - 382px))',
              transform: isIframeVisible ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 0.5s ease',
              zIndex: 9999
            }}
            title="Notification Menu"
          />
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: isWebViews || hideLeftMenu ? '1fr' : 'auto 1fr', height: 1, overflowX: 'hidden' }}>
        {isLogin && !hideLeftMenu && (
          <Drawer style={{ display: isWebViews ? 'none' : null }} className="sidebar" variant="permanent" open={!collapse}>
            <DrawerHeader
              className="navbarHeader"
              sx={{ backgroundColor: 'primary.main', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', minHeight: '64px', height: '64px' }}
            >
              {collapse ? (
                <img alt="navbar brand" className="navbar-brand" style={{ width: 41, height: 41, marginLeft: 3 }} src="../../img/miniLogo.png" />
              ) : (
                <img alt="navbar brand" className="navbar-brand" style={{ marginLeft: 14 }} src="../../img/logo.png" />
              )}
            </DrawerHeader>
            <Box
              sx={{
                backgroundColor: 'white',
                width: '280px',
                boxShadow: '0 0 6px rgba(0, 0, 0, 0.12)',
                height: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                overflowX: 'hidden'
              }}
            >
              <Box sx={{ mt: 1.9 }} />
              {isAdminShellMode
                ? getAdminShellMenuItems(t, selectedMenuItem).map((leftMenuItem, index) => (
                    <div
                      key={leftMenuItem.key}
                      style={{
                        position: 'relative',
                        textDecoration: 'none',
                        marginTop: '-4px',
                        marginBottom: '-2px',
                        marginLeft: '-1px',
                        backgroundColor: leftMenuItem.selected ? 'rgba(236, 239, 241, 0.5)' : '#fff0',
                        borderColor: 'rgb(229, 229, 229)',
                        borderBottomStyle: 'solid',
                        borderBottomWidth: leftMenuItem.selected ? '1px' : '0px'
                      }}
                    >
                      <a
                        className="menuItemLink"
                        href={`${leftMenuItem.fieldPie2 ? '' : process.env.REACT_APP_FIELDPIE_OLD_URL}${leftMenuItem.path}`}
                        style={{
                          display: 'block',
                          width: '100%',
                          height: '100%'
                        }}
                      >
                        <Tooltip arrow={true} title={collapse && leftMenuItem.label} placement={index === 0 ? 'bottom' : 'top'} style={{ float: 'left' }}>
                          <Box sx={{ height: '55px', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <Box
                              sx={{
                                ml: collapse ? 1.4 : 2,
                                mr: 2.5,
                                bgcolor: leftMenuItem.bgColor,
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <img alt={leftMenuItem.label} src={`../../img/${leftMenuItem.image}`} />
                            </Box>
                            <Box sx={{ fontSize: 14, color: leftMenuItem.selected ? 'rgb(29, 37, 44)' : '#455a64cc', fontWeight: '400' }}>
                              {!collapse && leftMenuItem.label}
                            </Box>
                          </Box>
                        </Tooltip>
                      </a>
                    </div>
                  ))
                : leftMenuItemDashboard
                    .slice(0, 1)
                    .filter((leftMenuItem) => leftMenuItem.visible)
                    .map((leftMenuItem, index) => (
                      <div
                        key={leftMenuItem.path}
                        style={{
                          position: 'relative',
                          textDecoration: 'none',
                          marginTop: '-4px',
                          marginBottom: '-2px',
                          marginLeft: '-1px',
                          backgroundColor: leftMenuItem.selected ? 'rgba(236, 239, 241, 0.5)' : '#fff0',
                          borderColor: 'rgb(229, 229, 229)',
                          borderBottomStyle: 'solid',
                          borderBottomWidth: leftMenuItem.selected ? '1px' : '0px'
                        }}
                      >
                        <a
                          className="menuItemLink"
                          href={`${leftMenuItem.fieldPie2 ? '' : process.env.REACT_APP_FIELDPIE_OLD_URL}${leftMenuItem.path}`}
                          style={{
                            display: 'block',
                            width: '100%',
                            height: '100%'
                          }}
                        >
                          <Tooltip arrow={true} title={collapse && leftMenuItem.label} placement={index === 0 ? 'bottom' : 'top'} style={{ float: 'left' }}>
                            <Box sx={{ height: '55px', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  ml: collapse ? 1.4 : 2,
                                  mr: 2.5,
                                  bgcolor: leftMenuItem.bgColor,
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <img
                                  alt={leftMenuItem.label}
                                  src={`../../img/${leftMenuItem.image}`}
                                  style={{ marginLeft: leftMenuItem.path === '/tasks/' ? '3px' : '0' }}
                                />
                              </Box>
                              {
                                <Box sx={{ fontSize: 14, color: leftMenuItem.selected ? 'rgb(29, 37, 44)' : '#455a64cc', fontWeight: '400' }}>
                                  {!collapse && leftMenuItem.label}
                                </Box>
                              }
                            </Box>
                          </Tooltip>
                        </a>
                        {leftMenuItem.label === t('dashboard_menu') && hasCustomDashboardViewRight && <FavoriteDashboardButton />}
                      </div>
                    ))}
              {!isAdminShellMode && (
                <>
                  <Box sx={{ mt: -1 }} />
                  <LeftMenu
                    leftMenuItems={sortedLeftMenuItems}
                    collapse={collapse}
                    toggleMenu={toggleMenu}
                    t={t}
                    selectedMenuItem={selectedMenuItem}
                  ></LeftMenu>
                </>
              )}
            </Box>
            <IconButton className="toogle" onClick={toggleMenu}>
              {collapse ? <ChevronRightIcon style={{ height: '24px', width: '24px' }} /> : <ChevronLeftIcon style={{ height: '24px', width: '24px' }} />}
            </IconButton>
          </Drawer>
        )}
        <Box
          className={isWebViews ? 'mobile-children' : 'children'}
          sx={{
            flexGrow: 1,
            overflow: isIframeVisible || (
              isWebViews &&
              new URLSearchParams(window.location.search).get('mode') === 'popup' &&
              (
                window.location.pathname.includes('trialonboardingvideoplayer') ||
                new URLSearchParams(window.location.search).get('component') === 'trialonboardingvideoplayer'
              )
            ) ? 'hidden' : 'auto',
            height: isWebViews &&
              new URLSearchParams(window.location.search).get('mode') === 'popup'
              ? 1
              : isMobileView ? '99%' : 1
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

function FavoriteDashboards({ dashboards, collapse }) {
  const [hovered, setHovered] = useState(null);
  const { t } = useTranslation();

  return (
    <div
      style={{
        position: 'fixed',
        top: '78px',
        left: collapse ? '65px' : '240px',
        backgroundColor: '#fff',
        boxShadow: '0px 0px 8px rgba(0,0,0,0.1)',
        zIndex: 100,
        maxWidth: '1000px',
        minWidth: '200px',
        maxHeight: '590px',
        overflowY: 'auto'
      }}
    >
      {dashboards.map((dashboard, index) => {
        if (index === 0) {
          return (
            <div
              key={dashboard.id}
              style={{
                paddingRight: '10px',
                paddingLeft: '10px',
                paddingTop: '10px',
                paddingBottom: '10px',
                textDecoration: 'none',
                color: '#009587',
                fontSize: '14px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'default',
                borderBottom: dashboards.length - 1 === index ? 'none' : '1px solid #eee'
              }}
              onMouseEnter={() => setHovered(dashboard.id)}
              onMouseLeave={() => setHovered(null)}
            >
              {t(`${dashboard.name}`)}
            </div>
          );
        } else {
          return (
            <a
              key={dashboard.id}
              href={`/dashboard/${dashboard.id}`}
              style={{
                display: 'block',
                paddingRight: '10px',
                paddingLeft: '10px',
                paddingTop: '10px',
                paddingBottom: '10px',
                textDecoration: 'none',
                color: '#333',
                fontSize: '14px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                borderBottom: dashboards.length - 1 === index ? 'none' : '1px solid #eee',
                backgroundColor: hovered === dashboard.id ? '#f5f5f5' : 'transparent'
              }}
              onMouseEnter={() => setHovered(dashboard.id)}
              onMouseLeave={() => setHovered(null)}
            >
              {dashboard.name}
            </a>
          );
        }
      })}
    </div>
  );
}

AppMenu.propTypes = {
  children: PropTypes.object
};
